import type { GameDefinition, Outcome, Seat, Viewer } from '@campfire/engine';

/** Peg colors in palette order. The letter keeps the game playable without color vision. */
export const PEGS = [
  { name: 'Red', letter: 'R' },
  { name: 'Blue', letter: 'B' },
  { name: 'Green', letter: 'G' },
  { name: 'Yellow', letter: 'Y' },
  { name: 'Purple', letter: 'P' },
  { name: 'Orange', letter: 'O' },
  { name: 'Teal', letter: 'T' },
  { name: 'Pink', letter: 'K' },
] as const;

export interface MastermindOptions {
  readonly length: 4 | 5;
  readonly colors: 6 | 8;
  readonly duplicates: boolean;
  readonly guesses: 8 | 10 | 12;
}

export interface Feedback {
  /** Right color, right place. */
  readonly black: number;
  /** Right color, wrong place. */
  readonly white: number;
}

export interface GuessRecord extends Feedback {
  readonly seat: Seat;
  readonly guess: readonly number[];
}

export interface RoundRecord {
  /** null in a solo game, where the app is the codemaker. */
  readonly codemaker: Seat | null;
  readonly secret: readonly number[];
  readonly guesses: readonly GuessRecord[];
  readonly broken: boolean;
  /** Who earned the round's points: the breaker who cracked it, else the codemaker (null solo). */
  readonly scorer: Seat | null;
  /** Points `scorer` earned (0 in a solo game). */
  readonly points: number;
}

export interface MastermindState {
  readonly options: MastermindOptions;
  readonly seats: number;
  /** 0-based index of the round in play. */
  readonly round: number;
  readonly codemaker: Seat | null;
  /** null while the codemaker has yet to set it. */
  readonly secret: readonly number[] | null;
  readonly guesses: readonly GuessRecord[];
  readonly scores: readonly number[];
  readonly history: readonly RoundRecord[];
  readonly result: Outcome | null;
}

export type MastermindAction =
  | { readonly type: 'code'; readonly code: readonly number[] }
  | { readonly type: 'guess'; readonly guess: readonly number[] };

export interface MastermindView {
  readonly you: Seat | null;
  readonly options: MastermindOptions;
  readonly round: number;
  readonly rounds: number;
  readonly codemaker: Seat | null;
  readonly phase: 'set' | 'break' | 'over';
  /** The secret: only for the codemaker, and for everyone once the round is over. */
  readonly secret: readonly number[] | null;
  readonly guesses: readonly GuessRecord[];
  /** Seat that acts now, or null when the game is over. */
  readonly turn: Seat | null;
  readonly breakers: readonly Seat[];
  readonly scores: readonly number[];
  readonly history: readonly RoundRecord[];
  readonly result: Outcome | null;
}

/** Standard Mastermind feedback; correct when either side repeats a color. */
export function scoreGuess(secret: readonly number[], guess: readonly number[]): Feedback {
  let black = 0;
  const left: number[] = [];
  const spare: number[] = [];
  for (let i = 0; i < secret.length; i++) {
    if (secret[i] === guess[i]) black++;
    else {
      left[secret[i] as number] = (left[secret[i] as number] ?? 0) + 1;
      spare.push(guess[i] as number);
    }
  }
  let white = 0;
  for (const g of spare) {
    if ((left[g] ?? 0) > 0) {
      left[g] = (left[g] as number) - 1;
      white++;
    }
  }
  return { black, white };
}

const rounds = (seats: number) => (seats >= 2 ? seats : 1);
export const breakersOf = (seats: number, codemaker: Seat | null): Seat[] =>
  Array.from({ length: seats }, (_, i) => i).filter((i) => i !== codemaker);

/** Why `code` is not a legal secret or guess, or null. Duplicates only restrict the secret. */
function codeProblem(code: readonly number[], o: MastermindOptions, isSecret: boolean): string | null {
  if (!Array.isArray(code) || code.length !== o.length) return `use exactly ${o.length} pegs`;
  if (!code.every((c) => Number.isInteger(c) && c >= 0 && c < o.colors)) return 'unknown color';
  if (isSecret && !o.duplicates && new Set(code).size !== code.length) return 'the secret may not repeat a color';
  return null;
}

function randomSecret(o: MastermindOptions, rng: { int(n: number): number }): number[] {
  const pool = Array.from({ length: o.colors }, (_, i) => i);
  const out: number[] = [];
  for (let i = 0; i < o.length; i++) {
    const k = rng.int(pool.length);
    out.push(pool[k] as number);
    if (!o.duplicates) pool.splice(k, 1);
  }
  return out;
}

/** An evenly spread, bounded sample of codes (full enumeration is too large for 5 pegs of 8). */
function sampleCodes(o: MastermindOptions, isSecret: boolean, n = 24): number[][] {
  const total = o.colors ** o.length;
  const seen = new Set<string>();
  const out: number[][] = [];
  for (let k = 0; k < n; k++) {
    let x = Math.floor((k * total) / n);
    const code: number[] = [];
    for (let i = 0; i < o.length; i++) {
      code.push(x % o.colors);
      x = Math.floor(x / o.colors);
    }
    const key = code.join();
    if (!seen.has(key) && codeProblem(code, o, isSecret) === null) {
      seen.add(key);
      out.push(code);
    }
  }
  if (out.length === 0) out.push(Array.from({ length: o.length }, (_, i) => i));
  return out;
}

const turnOf = (s: MastermindState): Seat | null => {
  if (s.result) return null;
  if (s.secret === null) return s.codemaker;
  const b = breakersOf(s.seats, s.codemaker);
  return b[s.guesses.length % b.length] as Seat;
};

export const mastermind: GameDefinition<MastermindState, MastermindAction, MastermindView, MastermindOptions> = {
  id: 'mastermind',
  name: 'Mastermind',
  rules: [
    'One player (the codemaker) hides a secret code of colored pegs. The others try to crack it in a limited number of guesses. Alone, the app makes the code and you crack it.',
    'Each guess is a row of pegs. Every color also has a letter, so you never have to rely on color alone.',
    'After each guess you get key pegs. A black key peg means one peg is the right color in the right place. A white key peg means one peg is the right color in the wrong place. The key pegs do not say which pegs they are about.',
    'A color is only counted as many times as it appears in the secret, so repeated colors in a guess never earn extra key pegs.',
    'With several players the codemaker role rotates. In each round the codemaker sets the code in secret, then the other players take turns making guesses until it is cracked or the guesses run out. Any breaker may guess on their turn.',
    'The breaker who cracks the code scores the guesses left over plus one (crack it first try with 10 guesses for 10 points; on the last guess for 1). A code that is never cracked earns its maker 1 point. Everyone is codemaker once, then the highest score wins. A tie is a draw.',
    'Settings: code length (4 or 5), number of colors (6 or 8), whether the secret may repeat a color (guesses may always repeat), and number of guesses (8, 10 or 12).',
    'The app keeps the secret hidden from the breakers and enforces the rules.',
  ],
  minSeats: 1,
  maxSeats: 6,
  defaultOptions: { length: 4, colors: 6, duplicates: true, guesses: 10 },
  optionSpecs: [
    {
      key: 'length',
      label: 'Code length',
      choices: [
        { value: 4, label: '4 pegs' },
        { value: 5, label: '5 pegs' },
      ],
    },
    {
      key: 'colors',
      label: 'Colors',
      choices: [
        { value: 6, label: '6 colors' },
        { value: 8, label: '8 colors' },
      ],
    },
    {
      key: 'duplicates',
      label: 'Repeated colors in the secret',
      help: 'Guesses may always repeat a color.',
      choices: [
        { value: true, label: 'Allowed' },
        { value: false, label: 'Not allowed' },
      ],
    },
    {
      key: 'guesses',
      label: 'Guesses per round',
      choices: [
        { value: 8, label: '8 guesses' },
        { value: 10, label: '10 guesses' },
        { value: 12, label: '12 guesses' },
      ],
    },
  ],

  setup: ({ seats, options, rng }) => ({
    options,
    seats,
    round: 0,
    codemaker: seats >= 2 ? 0 : null,
    secret: seats >= 2 ? null : randomSecret(options, rng),
    guesses: [],
    scores: Array.from({ length: seats }, () => 0),
    history: [],
    result: null,
  }),

  activeSeats(s) {
    const t = turnOf(s);
    return t === null ? [] : [t];
  },

  validate(s, a, seat) {
    if (s.result) return { ok: false, reason: 'game over' };
    if (seat !== turnOf(s)) return { ok: false, reason: 'not your turn' };
    if (a.type === 'code') {
      if (s.secret !== null) return { ok: false, reason: 'the code is already set' };
      const p = codeProblem(a.code, s.options, true);
      return p ? { ok: false, reason: p } : { ok: true };
    }
    if (a.type !== 'guess') return { ok: false, reason: 'unknown action' };
    if (s.secret === null) return { ok: false, reason: 'wait for the codemaker' };
    const p = codeProblem(a.guess, s.options, false);
    return p ? { ok: false, reason: p } : { ok: true };
  },

  apply(s, a) {
    if (a.type === 'code') return { ...s, secret: [...a.code] };
    const secret = s.secret as readonly number[];
    const seat = turnOf(s) as Seat;
    const guesses = [...s.guesses, { seat, guess: [...a.guess], ...scoreGuess(secret, a.guess) }];
    const broken = (guesses[guesses.length - 1] as GuessRecord).black === s.options.length;
    if (!broken && guesses.length < s.options.guesses) return { ...s, guesses };

    const solo = s.codemaker === null;
    // Breaker scoring (FR-MASTERMIND-11/12): the breaker who cracks it earns one point per guess
    // they had to spare plus one; a code that survives every guess earns its maker 1 point.
    const scorer = solo ? null : broken ? seat : s.codemaker;
    const points = solo ? 0 : broken ? s.options.guesses + 1 - guesses.length : 1;
    const scores = s.scores.map((v, i) => (i === scorer ? v + points : v));
    const history = [...s.history, { codemaker: s.codemaker, secret, guesses, broken, scorer, points }];
    const round = s.round + 1;
    if (round < rounds(s.seats)) return { ...s, round, codemaker: round, secret: null, guesses: [], scores, history };

    let result: Outcome;
    if (solo) {
      result = broken
        ? { winners: [0], draw: false, reason: `cracked in ${guesses.length}`, scores: { 0: guesses.length } }
        : { winners: [], draw: true, reason: 'not cracked', scores: { 0: guesses.length } };
    } else {
      const best = Math.max(...scores);
      const winners = scores.flatMap((v, i) => (v === best ? [i] : []));
      result = winners.length === 1 ? { winners, draw: false, reason: 'most points', scores: { ...scores } } : { winners: [], draw: true, reason: 'tied', scores: { ...scores } };
    }
    return { ...s, round, guesses: [], secret: null, scores, history, result };
  },

  view(s, viewer: Viewer): MastermindView {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const over = s.result !== null;
    return {
      you,
      options: s.options,
      round: Math.min(s.round, rounds(s.seats) - 1),
      rounds: rounds(s.seats),
      codemaker: s.codemaker,
      phase: over ? 'over' : s.secret === null ? 'set' : 'break',
      secret: !over && you !== null && you === s.codemaker ? s.secret : null,
      guesses: s.guesses,
      turn: turnOf(s),
      breakers: breakersOf(s.seats, s.codemaker),
      scores: s.scores,
      history: s.history,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result || seat !== turnOf(s)) return [];
    return s.secret === null
      ? sampleCodes(s.options, true).map((code) => ({ type: 'code', code }))
      : sampleCodes(s.options, false).map((guess) => ({ type: 'guess', guess }));
  },
};
